import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { withRouter } from 'react-router-dom';
import { Message, Button, Confirm } from 'semantic-ui-react';
import { isEmpty } from 'lodash';

import ACCESS from '../constants/access';
import APP_PATH from '../constants/paths';
import client from '../apollo';
import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import NewFlatRateOffer from '../views/FlatRatesOffers/NewForm';
import NewFlatRateOfferForm from '../views/FlatRatesOffers/NewFlatRateOfferForm';
import EditOfferBatch from '../views/FlatRatesOffers/EditOfferBatch';
import FlatRateOfferBatchesList from '../views/FlatRatesOffers/FlatRateOfferBatchesList';
import createFlatRateOfferMutation from '../graphql/createFlatRateOfferMutation';
import flatRateOfferBatchesQuery from '../graphql/flatRateOfferBatchesQuery';
import flatRateOfferBatchQuery from '../graphql/flatRateOfferBatchQuery';
import resetFlatRateOfferBatchMutation from '../graphql/resetFlatRateOfferBatchMutation';
import confirmFlatRateOfferBatchMutation from '../graphql/confirmFlatRateOfferBatchMutation';
import deleteFlatRateOfferBatchMutation from '../graphql/deleteFlatRateOfferBatchMutation';
import updateFlatRateOfferBatchMutation from '../graphql/updateFlatRateOfferBatchMutation';
import addOfferInFlatRateOfferBatchMutation from '../graphql/addOfferInFlatRateOfferBatchMutation';
import acceptFlatRateOfferBatchMutation from '../graphql/acceptFlatRateOfferBatchMutation';
import rejectFlatRateOfferBatchMutation from '../graphql/rejectFlatRateOfferBatchMutation';

import { allowAccess } from '../utilities/user';
import Search from '../views/Shared/Search';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';

const isInvalidPriceInParams = flatRateOfferInput => {
  if (flatRateOfferInput && flatRateOfferInput.flatRateOffers) {
    return flatRateOfferInput.flatRateOffers.some(offer => (
      !offer.price
        || offer.price < 0
        || (offer.daysPsi && (!offer.psiBundlePrice || offer.psiBundlePrice < 0))
    ));
  }
  return true;
};

const FlatRateOffers = props => {
  const {
    data: { flatRateOfferBatches, refetch },
    searchFields,
    user,
    createMutation,
    updateMutation,
    rejectMutation,
    acceptMutation,
    addOfferMutation,
    deleteMutation,
    resetMutation,
    confrimMutation,
    setSearchFields: setSearchFieldsAction,
    setPage: setPageAction,
    history,
    match,
  } = props;

  const [flashMessage, setFlashMessage] = useState(null);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [selectedFlatRateOfferBatch, setSelectedFlatRateOfferBatch] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewFlatRateOfferFormOpen, setIsNewFlatRateOfferFormOpen] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  useEffect(() => {
    const batchId = match.params.id;
    if (!isEmpty(batchId)) {
      client
        .query({
          query: flatRateOfferBatchQuery,
          variables: {
            id: parseInt(batchId, 10),
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const { flatRateOfferBatch } = data;
          setIsEditModalOpen(true);
          setResetForm(true);
          setSelectedFlatRateOfferBatch(flatRateOfferBatch);
        });
    }
  }, [match.params.id]);

  const openNewForm = useCallback(() => {
    setIsNewModalOpen(true);
  }, []);

  const handleDismiss = useCallback(() => {
    setFlashMessage(null);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteConfirmationOpen(false);
  }, []);

  const onNewFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsNewModalOpen(false);
  }, []);

  const onNewFlatRateOfferFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsNewFlatRateOfferFormOpen(false);
  }, []);

  const onEditFormCloseHandler = useCallback(() => {
    setFormError(null);
    setSubmitting(null);
    setIsEditModalOpen(false);
  }, []);

  const resetFlatRateOfferBatch = useCallback(flatRateOfferBatch => {
    const { id } = flatRateOfferBatch;
    const input = { id };
    resetMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          resetFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          refetch();
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully Reset 360 Offer');
        } else {
          setSubmitting(false);
          setFlashMessage(errors);
        }
      })
      .catch(() => {
        setFlashMessage('Error while deleting 360 Offer');
      });
  }, [resetMutation, refetch]);

  const confirmFlatRateOfferBatch = useCallback(flatRateOfferBatch => {
    const { id } = flatRateOfferBatch;
    const input = { id };

    confrimMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          confirmFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          refetch();
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully Confirmed 360 Offer');
        } else {
          setSubmitting(false);
          setFlashMessage(errors);
        }
      })
      .catch(() => {
        setFlashMessage('Error while deleting 360 Offer');
      });
  }, [confrimMutation, refetch]);

  const onSelectFlatRateOfferBatch = useCallback((action, flatRateOfferBatch) => {
    setSelectedFlatRateOfferBatch(flatRateOfferBatch);

    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        break;
      case 'Reset':
        resetFlatRateOfferBatch(flatRateOfferBatch);
        break;
      case 'Confirm':
        confirmFlatRateOfferBatch(flatRateOfferBatch);
        break;
      case 'Delete':
        setIsDeleteConfirmationOpen(true);
        break;
      case 'AddNewOffer':
        setIsNewFlatRateOfferFormOpen(true);
        break;
      default:
        setSelectedFlatRateOfferBatch(null);
    }
  }, [resetFlatRateOfferBatch, confirmFlatRateOfferBatch]);

  const onNewFormSubmitHandler = useCallback(values => {
    const flatRateOfferInput = { ...values };
    setSubmitting(true);
    if (isInvalidPriceInParams(flatRateOfferInput)) {
      setSubmitting(false);
      setFormError('must be greater than 0');
      return;
    }
    createMutation({
      variables: { flatRateOfferInput },
    })
      .then(({ data }) => {
        const {
          createFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setFlashMessage('Successfully created 360 Offer');
          refetch();
          onNewFormCloseHandler();
        } else {
          setSubmitting(false);
          setFormError(errors);
        }
      })
      .catch(error => {
        setSubmitting(false);
        setFormError(error);
      });
  }, [createMutation, refetch, onNewFormCloseHandler]);

  const onEditFormSubmitHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);
    updateMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          updateFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setResetForm(false);
          setFlashMessage('Successfully updated 360 Offer');

          if (resetForm) {
            history.push(APP_PATH.DEALSHIELD360_OFFERS);
          } else {
            refetch();
          }
        } else {
          setSubmitting(false);
          setFormError(errors);
        }
      })
      .catch(error => {
        setSubmitting(false);
        setFormError(error);
      });
  }, [updateMutation, history, resetForm, refetch]);

  const onRejectHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    rejectMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          rejectFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully rejected 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(errors);
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [rejectMutation, refetch]);

  const onAcceptHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    acceptMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          acceptFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsEditModalOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully accepted 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(errors);
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [acceptMutation, refetch]);

  const onNewFlatRateOfferFormSubmitHandler = useCallback(values => {
    const input = { ...values };
    setSubmitting(true);

    addOfferMutation({
      variables: { input },
    })
      .then(({ data }) => {
        const {
          addOfferInFlatRateOfferBatch: { success, errors },
        } = data;
        if (success) {
          setSubmitting(false);
          setIsNewFlatRateOfferFormOpen(false);
          setSelectedFlatRateOfferBatch(null);
          setFlashMessage('Successfully added 360 Offer');
          refetch();
        } else {
          setSubmitting(false);
          setFormError(errors);
        }
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [addOfferMutation, refetch]);

  const onSearchSubmitHandler = useCallback(event => {
    const { auctionAccessNumber, dealerNumber } = event.target;
    const dealerNo = isEmpty(dealerNumber.value) ? null : dealerNumber.value;
    const variableArguments = {
      auctionAccessNumber: auctionAccessNumber.value || null,
      dealerNumber: dealerNo,
    };

    refetch({
      ...variableArguments,
    });

    setSearchFieldsAction(variableArguments);
    setPageAction({});
  }, [refetch, setSearchFieldsAction, setPageAction]);

  const deleteFlatRateOfferBatch = useCallback(() => {
    const { id } = selectedFlatRateOfferBatch;
    const input = { id };

    deleteMutation({
      variables: { input },
    })
      .then(() => {
        refetch();
        setSelectedFlatRateOfferBatch(null);
        setIsDeleteConfirmationOpen(false);
        setFlashMessage('Successfully Deleted 360 Offer');
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
      });
  }, [deleteMutation, selectedFlatRateOfferBatch, refetch]);

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleDismiss}
          content={flashMessage}
          positive
        />
      )}

      {isNewModalOpen && (
        <div>
          <NewFlatRateOffer
            isModalOpen={isNewModalOpen}
            onCloseHandler={onNewFormCloseHandler}
            onSubmitHandler={onNewFormSubmitHandler}
            error={formError}
            submitting={submitting}
          />
        </div>
      )}

      {isNewFlatRateOfferFormOpen && (
        <div>
          <NewFlatRateOfferForm
            isModalOpen={isNewFlatRateOfferFormOpen}
            onCloseHandler={onNewFlatRateOfferFormCloseHandler}
            onSubmitHandler={onNewFlatRateOfferFormSubmitHandler}
            error={formError}
            submitting={submitting}
            flatRateOfferBatch={selectedFlatRateOfferBatch}
          />
        </div>
      )}

      {isEditModalOpen && (
        <div>
          <EditOfferBatch
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            error={formError}
            submitting={submitting}
            flatRateOfferBatch={selectedFlatRateOfferBatch}
            resetForm={resetForm}
            onRejectHandler={onRejectHandler}
            onAcceptHandler={onAcceptHandler}
          />
        </div>
      )}
      {user && allowAccess(user, ACCESS.DEALSHIELD360_CREATE_OFFERS) && (
        <Button color='green' onClick={openNewForm} inverted>
          {' '}
          Extend DS Offer{' '}
        </Button>
      )}
      <br />
      <br />

      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['auctionAccessNumber', 'dealerNumber']}
      />

      <FlatRateOfferBatchesList
        onSelectFlatRateOfferBatch={onSelectFlatRateOfferBatch}
        flatRateOfferBatches={flatRateOfferBatches}
      />
      <Confirm
        open={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={deleteFlatRateOfferBatch}
      />
    </div>
  );
};

FlatRateOffers.propTypes = {
  data: PropTypes.shape({
    flatRateOfferBatches: PropTypes.array,
    refetch: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.shape({
    auctionAccessNumber: PropTypes.string,
    dealerNumber: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    roles: PropTypes.array,
  }),
  createMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  rejectMutation: PropTypes.func.isRequired,
  acceptMutation: PropTypes.func.isRequired,
  addOfferMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
  resetMutation: PropTypes.func.isRequired,
  confrimMutation: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

FlatRateOffers.defaultProps = {
  user: null,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(flatRateOfferBatchesQuery, 'flatRateOfferBatches'),
  graphql(createFlatRateOfferMutation, { name: 'createMutation' }),
  graphql(resetFlatRateOfferBatchMutation, { name: 'resetMutation' }),
  graphql(confirmFlatRateOfferBatchMutation, { name: 'confrimMutation' }),
  graphql(deleteFlatRateOfferBatchMutation, { name: 'deleteMutation' }),
  graphql(updateFlatRateOfferBatchMutation, { name: 'updateMutation' }),
  graphql(addOfferInFlatRateOfferBatchMutation, { name: 'addOfferMutation' }),
  graphql(acceptFlatRateOfferBatchMutation, { name: 'acceptMutation' }),
  graphql(rejectFlatRateOfferBatchMutation, { name: 'rejectMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(FlatRateOffers);
